import {Component, OnInit} from '@angular/core';
import {UserService} from '../../../services/user.service';
import {CurrentUserService} from '../../../services/current-user.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NotificationService} from '../../../services/notification.service';
import {Router} from '@angular/router';
import {UserDTO} from '../../../model/userDTO';
import {Location} from '@angular/common';
import {PasswordVerificationService} from '../../../services/password-verification.service';

@Component({
  templateUrl: './edit-user.component.html'
})
export class EditUserComponent implements OnInit {

  user: UserDTO;
  loading;
  form: FormGroup;

  constructor(private service: UserService,
              private builder: FormBuilder,
              private router: Router,
              private passwordVerificationService: PasswordVerificationService,
              private _location: Location,
              private notificationService: NotificationService,
              private currentUserService: CurrentUserService) {
  }

  get f() {
    return this.form.controls;
  }

  ngOnInit(): void {
    this.currentUserService.renew().then(user => {
      this.user = user;
      this.form = this.createForm(user);
    });
  }

  submit(value) {
    this.save(value, () => {
      this.currentUserService.renew().then(() => {
        this.notificationService.success('gegevens bijgewerkt');
        this.router.navigate(['/']);
      });
    });
  }

  changeEmail() {
    if (this.unsavedChanged()) {
      const text = {
        header: 'er zijn onopgeslagen wijzigingen',
        ok: 'wijzigingen opslaan'
      };
      this.notificationService.confirm(text)
        .then(() => this.save(this.form.value, () => this.openChangeEmailDialog()));
    } else {
      this.openChangeEmailDialog();
    }
  }

  private save(value, handleSuccess) {
    if (this.form.invalid || this.loading) {
      return;
    }
    this.loading = true;
    this.service.update(this.user.id, value).subscribe(
      () => handleSuccess(),
      error => this.notificationService.errorResponse(error),
      () => this.loading = false);
  }

  private createForm(user: UserDTO) {
    return this.builder.group({
      'showEmailOnReports': [user.showEmailOnReports],
      'firstNameInitials': [user.firstNameInitials, [Validators.required, Validators.maxLength(255)]],
      'firstName': [user.firstName, [Validators.required, Validators.maxLength(255)]],
      'insertion': [user.insertion, [Validators.maxLength(255)]],
      'lastName': [user.lastName, [Validators.required, Validators.maxLength(255)]],
      'street': [user.street, [Validators.required, Validators.maxLength(255)]],
      'houseNumber': [user.houseNumber, [Validators.required, Validators.maxLength(255)]],
      'houseNumberSuffix': [user.houseNumberSuffix, [Validators.maxLength(255)]],
      'zipCode': [user.zipCode, [Validators.required, Validators.maxLength(255)]],
      'city': [user.city, [Validators.required, Validators.maxLength(255)]],
      'phone': [user.phone, [Validators.required, Validators.maxLength(255)]],
    });
  }

  private unsavedChanged() {
    const value = this.form.value;
    return !(this.user.showEmailOnReports === value.showEmailOnReports &&
      this.user.firstNameInitials === value.firstNameInitials &&
      this.user.firstName === value.firstName &&
      this.user.insertion === value.insertion &&
      this.user.lastName === value.lastName &&
      this.user.street === value.street &&
      this.user.houseNumber === value.houseNumber &&
      this.user.houseNumberSuffix === value.houseNumberSuffix &&
      this.user.zipCode === value.zipCode &&
      this.user.city === value.city &&
      this.user.phone === value.phone);
  }

  private openChangeEmailDialog() {
    this.router.navigate(['/e-mailadres-aanpassen']);
  }

  back() {
    this._location.back();
  }

  requestDataDeletion() {
    this.notificationService.confirm({}).then(() => {
      this.passwordVerificationService.checkPassword()
        .then(() => {
          this.service.requestDataDeletion(this.user.id).subscribe(() => {
            const text = {
              header: 'uw verwijderverzoek is aangemaakt',
              body: 'wij zullen uw aanvraag binnen 5 werkdagen behandelen'
            };
            this.notificationService.inform(text)
              .finally(() => this.ngOnInit());
          });
        });
    });
  }

  undoRequestDataDeletion() {
    this.notificationService.confirm({}).then(() => {
      this.passwordVerificationService.checkPassword().then(() => {
        this.service.undoRequestDataDeletion(this.user.id).subscribe(() => {
          const text = {header: 'uw verwijderverzoek is ongedaan gemaakt'};
          this.notificationService.inform(text)
            .finally(() => this.ngOnInit());
        });
      });
    });
  }

  changePassword() {
    this.service.recover({email: this.user.username}).subscribe(
      () => this.notificationService.inform({header: 'er is een e-mail aan u verzonden met verdere instructies'}),
      response => this.notificationService.errorResponse(response)
    );
  }
}
